import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { devAdminAPI } from '../../../services/adminService';
import MultiMediaDisplay from '../../../components/social/posts/MultiMediaDisplay';
import './PostModerationManager.css';

const PostModerationManager = ({ mode = 'all' }) => {
  const { t } = useTranslation();

  const [posts, setPosts] = useState([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedPost, setSelectedPost] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, post: null });
  const [ignoreConfirmOpen, setIgnoreConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteReasons, setDeleteReasons] = useState([]);
  const [otherReasonText, setOtherReasonText] = useState('');
  const [deleteImages, setDeleteImages] = useState([]);
  const [deleteImagePreviews, setDeleteImagePreviews] = useState([]);
  const deleteFileInputRef = useRef(null);

  const [isActionProcessing, setIsActionProcessing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [countryFilter, setCountryFilter] = useState('');
  const [availableCountries, setAvailableCountries] = useState([]);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  // Load posts
  const loadPosts = useCallback(async (page = 1) => {
    setIsLoadingPosts(true);
    setApiError(null);
    try {
      const params = {
        page,
        ...(mode === 'reported' ? { reported: 1 } : {}),
        ...(searchQuery ? { search: searchQuery } : {}),
        ...(countryFilter ? { country: countryFilter } : {}),
      };
      const response = await devAdminAPI.getPosts(params);
      const data = response?.data;
      const postList = data?.data?.data || data?.data || [];
      const paginationData = data?.data?.pagination || data?.pagination || { current_page: 1, last_page: 1, total: 0 };
      setPosts(postList);
      setPagination(paginationData);
      setAvailableCountries(data?.data?.available_countries || []);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to load posts:', error);
      setApiError(t('adminDashboard.postModeration.loadError', 'Failed to load posts.'));
    } finally {
      setIsLoadingPosts(false);
    }
  }, [mode, searchQuery, countryFilter, t]);

  // Load posts on mount and when mode/search/country changes
  useEffect(() => {
    setCurrentPage(1);
    loadPosts(1);
  }, [loadPosts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadPosts(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, loadPosts]);

  const handleSelectPost = async (post) => {
    setSelectedPost(post);
    setDeleteError(null);
    setIsLoadingDetails(true);

    try {
      const response = await devAdminAPI.getPostDetails(post.id);
      const data = response?.data?.data || response?.data;
      setSelectedPost(prev => ({
        ...prev,
        ...data,
        id: data?.id || prev?.id,
      }));
    } catch (error) {
      console.error('Failed to load post details:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleDeleteCheck = (post) => {
    setDeleteModal({ isOpen: true, post });
    setDeleteError(null);
    setDeleteReasons([]);
    setOtherReasonText('');
  };

  const REPORT_REASONS = [
    { value: 'spam', label: t('reportReasons.spam', 'Spam') },
    { value: 'inappropriate', label: t('reportReasons.inappropriate', 'Inappropriate Content') },
    { value: 'harassment', label: t('reportReasons.harassment', 'Harassment or Bullying') },
    { value: 'fake_info', label: t('reportReasons.fake_info', 'Fake Information') },
    { value: 'violence', label: t('reportReasons.violence', 'Violence or Threats') },
    { value: 'copyright', label: t('reportReasons.copyright', 'Copyright Violation') },
    { value: 'animal_cruelty', label: t('reportReasons.animal_cruelty', 'Animal Cruelty') },
    { value: 'other', label: t('reportReasons.other', 'Other') },
  ];

  const toggleReason = (value) => {
    setDeleteReasons(prev =>
      prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value]
    );
  };

  const handleDeleteImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const combined = [...deleteImages, ...files].slice(0, 5);
    setDeleteImages(combined);
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setDeleteImagePreviews(prev => [...prev, ...newPreviews].slice(0, 5));
  };

  const removeDeleteImage = (index) => {
    setDeleteImages(prev => prev.filter((_, i) => i !== index));
    setDeleteImagePreviews(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleConfirmDelete = async () => {
    const post = deleteModal.post;
    if (!post || isDeleting) return;

    setIsDeleting(true);
    try {
      const reasons = deleteReasons.length > 0 ? deleteReasons : undefined;
      const customReason = otherReasonText.trim() || undefined;
      await devAdminAPI.deletePost(post.id, { reasons, reason: customReason, images: deleteImages });
      setDeleteModal({ isOpen: false, post: null });
      setSelectedPost(null);
      setDeleteReasons([]);
      setOtherReasonText('');
      deleteImagePreviews.forEach(url => URL.revokeObjectURL(url));
      setDeleteImagePreviews([]);
      setDeleteImages([]);
      loadPosts(currentPage);
    } catch (error) {
      console.error('Post delete failed:', error);
      setDeleteError(error?.response?.data?.message || t('adminDashboard.postModeration.deleteFailed', 'Deletion failed.'));
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredCountries = availableCountries.filter(c => {
    if (!countrySearch) return true;
    return c.name?.toLowerCase().includes(countrySearch.toLowerCase());
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return isNaN(d) ? dateStr : d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getUserName = (user) => {
    if (!user) return 'Unknown';
    const first = typeof user.first_name === 'string' ? user.first_name : '';
    const last = typeof user.last_name === 'string' ? user.last_name : '';
    if (first && last) return `${first} ${last}`;
    if (first) return first;
    return 'Unknown';
  };

  const getInitial = (user) => {
    const name = getUserName(user);
    return name.charAt(0).toUpperCase();
  };

  const handleAction = async (actionKey) => {
    if (!selectedPost || isActionProcessing) return;
    setIsActionProcessing(true);

    try {
      switch (actionKey) {
        case 'ignore':
          await devAdminAPI.ignorePostReports(selectedPost.id);
          break;
        default:
          break;
      }
      loadPosts(currentPage);
    } catch (error) {
      console.error('Action failed:', error);
      setDeleteError(error?.response?.data?.message || 'Action failed. Please try again.');
    } finally {
      setIsActionProcessing(false);
    }
  };

  return (
    <div className="pm">
      <div className="pm-layout">
        {/* Left Panel — review queue */}
        <div className="pm-panel pm-queue">
          <div className="pm-panel-header">
            <h3 className="pm-panel-title">
              {mode === 'reported'
                ? t('adminDashboard.postModeration.reportedPosts', 'Reported Posts')
                : t('adminDashboard.postModeration.allPosts', 'All Posts')}
            </h3>
            <span className="pm-count">{pagination.total}</span>
          </div>

          <div className="pm-toolbar">
            <div className="pm-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder={t('adminDashboard.postModeration.searchPlaceholder', 'Search posts or authors...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="pm-search-clear"
                  onClick={() => setSearchQuery('')}
                  title={t('common.clear', 'Clear')}
                >
                  ×
                </button>
              )}
            </div>
            {/* Country Filter */}
            <div className="pm-filter">
              <button
                className={`pm-filter-toggle ${countryFilter ? 'active' : ''}`}
                onClick={() => setCountryDropdownOpen(o => !o)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
                <span className="pm-filter-toggle-label">
                  {countryFilter
                    ? availableCountries.find(c => String(c.id) === String(countryFilter))?.name || t('adminDashboard.userManagement.country', 'Country')
                    : t('adminDashboard.userManagement.allCountries', 'All Countries')}
                </span>
                <svg
                  className={`pm-filter-chevron ${countryDropdownOpen ? 'open' : ''}`}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {countryDropdownOpen && (
                <div className="pm-filter-dropdown">
                  <div className="pm-filter-search">
                    <input
                      type="text"
                      placeholder={t('adminDashboard.userManagement.searchCountry', 'Search country...')}
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="pm-filter-list">
                    <button
                      className={`pm-filter-item ${!countryFilter ? 'active' : ''}`}
                      onClick={() => { setCountryFilter(''); setCountryDropdownOpen(false); setCountrySearch(''); }}
                    >
                      {t('adminDashboard.userManagement.allCountries', 'All Countries')}
                    </button>
                    {filteredCountries.map(country => (
                      <button
                        key={country.id}
                        className={`pm-filter-item ${String(countryFilter) === String(country.id) ? 'active' : ''}`}
                        onClick={() => { setCountryFilter(String(country.id)); setCountryDropdownOpen(false); setCountrySearch(''); }}
                      >
                        {country.name}
                      </button>
                    ))}
                  </div>
                  {countryFilter && (
                    <button
                      className="pm-filter-clear"
                      onClick={() => { setCountryFilter(''); setCountryDropdownOpen(false); setCountrySearch(''); }}
                    >
                      {t('adminDashboard.userManagement.clearFilter', 'Clear filter')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="pm-queue-scroll">
            {isLoadingPosts ? (
              <div className="pm-queue-state">{t('adminDashboard.postModeration.loading', 'Loading posts...')}</div>
            ) : apiError ? (
              <div className="pm-queue-state">
                <p>{apiError}</p>
                <button className="pm-btn" onClick={() => loadPosts(currentPage)}>{t('common.retry', 'Retry')}</button>
              </div>
            ) : posts.length === 0 ? (
              <div className="pm-queue-state">
                <p>{t('adminDashboard.postModeration.noPosts', 'No posts found.')}</p>
              </div>
            ) : (
              posts.map(post => (
                <div
                  key={post.id}
                  className={`pm-queue-item ${selectedPost?.id === post.id ? 'selected' : ''}`}
                  onClick={() => handleSelectPost(post)}
                >
                  <div className="pm-queue-thumb">
                    {post.media_preview ? (
                      <img src={post.media_preview} alt="" />
                    ) : (
                      <div className="pm-queue-thumb-placeholder">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="pm-queue-info">
                    <span className="pm-queue-author">{getUserName(post.user)}</span>
                    <p className="pm-queue-excerpt">{stripHtml(post.content) || '—'}</p>
                    <span className="pm-queue-time">{formatDate(post.created_at)}</span>
                  </div>
                  {post.reports_count > 0 && (
                    <span className="pm-report-badge">
                      <span className="pm-report-badge-dot"></span>
                      {post.reports_count}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>

          {!isLoadingPosts && !apiError && posts.length > 0 && pagination.last_page > 1 && (
            <div className="pm-pagination">
              <button
                disabled={currentPage <= 1}
                onClick={() => loadPosts(currentPage - 1)}
              >
                {t('common.prev', 'Prev')}
              </button>
              <span>{currentPage} / {pagination.last_page}</span>
              <button
                disabled={currentPage >= pagination.last_page}
                onClick={() => loadPosts(currentPage + 1)}
              >
                {t('common.next', 'Next')}
              </button>
            </div>
          )}
        </div>

        {/* Right Panel — Post Details */}
        <div className="pm-panel pm-detail">
          {!selectedPost ? (
            <div className="pm-detail-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <p>{t('adminDashboard.postModeration.selectPost', 'Select a post to view details and manage it.')}</p>
            </div>
          ) : (
            <>
              <div className="pm-detail-header">
                <div className="pm-detail-header-main">
                  {selectedPost.user?.profile_image_url ? (
                    <img
                      src={selectedPost.user.profile_image_url}
                      alt={getUserName(selectedPost.user)}
                      className="pm-detail-avatar"
                    />
                  ) : (
                    <div className="pm-detail-avatar pm-detail-avatar-placeholder">
                      {getInitial(selectedPost.user)}
                    </div>
                  )}
                  <div className="pm-detail-header-text">
                    <h2>{getUserName(selectedPost.user)}</h2>
                    <p className="pm-detail-subtitle">
                      {formatDate(selectedPost.created_at)}
                      {selectedPost.reports?.length > 0 && (
                        <span className="pm-detail-report-count">
                          {' • '}
                          {selectedPost.reports.length} {t('adminDashboard.postModeration.reports', 'report(s)')}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="pm-detail-actions">
                  <button
                    className="pm-btn"
                    onClick={() => setIgnoreConfirmOpen(true)}
                    disabled={isActionProcessing}
                  >
                    {t('moderation.actions.ignore', 'Ignore Reports')}
                  </button>
                  <button
                    className="pm-btn pm-btn-danger"
                    onClick={() => handleDeleteCheck(selectedPost)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                    {t('moderation.actions.deletePost', 'Delete Post')}
                  </button>
                </div>
              </div>

              <div className="pm-detail-scroll">
                {isLoadingDetails ? (
                  <div className="pm-queue-state">{t('adminDashboard.postModeration.loadingDetails', 'Loading details...')}</div>
                ) : (
                  <>
                    {/* Slim fact strip */}
                    <div className="pm-facts">
                      <div className="pm-fact">
                        <svg className="pm-fact-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                        </svg>
                        <span className="pm-fact-value">{selectedPost.reactions_count ?? 0}</span>
                        <span className="pm-fact-label">{t('adminDashboard.postModeration.reactions', 'Reactions')}</span>
                      </div>
                      <div className="pm-fact">
                        <svg className="pm-fact-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        <span className="pm-fact-value">{selectedPost.comments_count ?? 0}</span>
                        <span className="pm-fact-label">{t('adminDashboard.postModeration.comments', 'Comments')}</span>
                      </div>
                      <div className="pm-fact">
                        <svg className="pm-fact-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                        </svg>
                        <span className="pm-fact-value">
                          {selectedPost.average_rating !== null && selectedPost.average_rating !== undefined ? (
                            <>
                              {Number(selectedPost.average_rating).toFixed(1)}
                              <svg className="pm-rating-star" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                              </svg>
                            </>
                          ) : (
                            <span className="pm-fact-none">{t('adminDashboard.postModeration.noRatings', 'No ratings')}</span>
                          )}
                        </span>
                        <span className="pm-fact-label">{t('adminDashboard.postModeration.rating', 'Rating')}</span>
                      </div>
                    </div>

                    {/* Report reasons summary */}
                    {selectedPost.reports && selectedPost.reports.length > 0 && (
                      <div className="pm-reason-chips">
                        {Object.entries(selectedPost.reports.reduce((acc, report) => {
                          const key = report.reason || '—';
                          acc[key] = (acc[key] || 0) + 1;
                          return acc;
                        }, {})).map(([reason, count]) => (
                          <span key={reason} className="pm-status is-danger">
                            {reason} ({count})
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Two-column layout: Content+Media (left) | Comments (right) */}
                    <div className="pm-detail-columns">
                      {/* Left column */}
                      <div className="pm-detail-left">
                        {/* Post Content */}
                        <div className="pm-section">
                          <h3 className="pm-section-title">{t('adminDashboard.postModeration.content', 'Content')}</h3>
                          {selectedPost.content ? (
                            <div
                              className="pm-content-text"
                              dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                            />
                          ) : (
                            <p className="pm-content-text">—</p>
                          )}
                        </div>

                        {/* Media Gallery */}
                        {selectedPost.media && selectedPost.media.length > 0 && (
                          <div className="pm-section">
                            <h3 className="pm-section-title">{t('adminDashboard.postModeration.media', 'Media')}</h3>
                            <div className="pm-media">
                              <MultiMediaDisplay
                                media={selectedPost.media.map(m => ({
                                  url: m.url,
                                  type: m.file_type,
                                  file_type: m.file_type,
                                }))}
                                slideHeight={200}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right column — Comments */}
                      <div className="pm-detail-right">
                        {selectedPost.comments && selectedPost.comments.length > 0 ? (
                          <div className="pm-section pm-comments">
                            <h3 className="pm-section-title">
                              {t('adminDashboard.postModeration.commentsSection', 'Comments')} ({selectedPost.comments.length})
                            </h3>
                            <div className="pm-comments-scroll">
                              {selectedPost.comments.map(comment => (
                                <div key={comment.id} className="pm-comment">
                                  <div className="pm-comment-header">
                                    {comment.user?.profile_image_url ? (
                                      <img src={comment.user.profile_image_url} alt="" className="pm-comment-avatar" />
                                    ) : (
                                      <div className="pm-comment-avatar-placeholder">
                                        {(comment.user?.name || 'U').charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    <div className="pm-comment-meta">
                                      <span className="pm-comment-author">{comment.user?.name || 'Unknown'}</span>
                                      <span className="pm-comment-date">{formatDate(comment.created_at)}</span>
                                    </div>
                                  </div>
                                  <div className="pm-comment-text">{comment.text || '—'}</div>

                                  {/* Replies */}
                                  {comment.replies && comment.replies.length > 0 && (
                                    <div className="pm-replies">
                                      {comment.replies.map(reply => (
                                        <div key={reply.id} className="pm-reply">
                                          <div className="pm-comment-header">
                                            {reply.user?.profile_image_url ? (
                                              <img src={reply.user.profile_image_url} alt="" className="pm-comment-avatar" />
                                            ) : (
                                              <div className="pm-comment-avatar-placeholder">
                                                {(reply.user?.name || 'U').charAt(0).toUpperCase()}
                                              </div>
                                            )}
                                            <div className="pm-comment-meta">
                                              <span className="pm-comment-author">{reply.user?.name || 'Unknown'}</span>
                                              <span className="pm-comment-date">{formatDate(reply.created_at)}</span>
                                            </div>
                                          </div>
                                          <div className="pm-comment-text">{reply.text || '—'}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="pm-section pm-comments">
                            <h3 className="pm-section-title">{t('adminDashboard.postModeration.commentsSection', 'Comments')}</h3>
                            <div className="pm-empty">
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                              </svg>
                              <p className="pm-empty-text">{t('adminDashboard.postModeration.noComments', 'No comments yet.')}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Reports */}
                    {selectedPost.reports && selectedPost.reports.length > 0 && (
                      <div className="pm-section">
                        <h3 className="pm-section-title">{t('adminDashboard.postModeration.reportsSection', 'Reports')}</h3>
                        <div className="pm-reports">
                          {selectedPost.reports.map(report => (
                            <div key={report.id} className="pm-report-card">
                              <div className="pm-report-card-header">
                                <span className="pm-report-reporter">
                                  {report.reporter?.name || t('adminDashboard.postModeration.unknownReporter', 'Unknown')}
                                </span>
                                <span className="pm-report-date">
                                  {formatDate(report.created_at)}
                                </span>
                              </div>
                              <div className="pm-report-reason">
                                <span className="pm-status is-danger">{report.reason || '—'}</span>
                              </div>
                              {report.description && (
                                <div className="pm-report-description">
                                  {report.description}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Modal with Reason */}
      {deleteModal.isOpen && (
        <div className="pm-modal-overlay" onClick={() => !isDeleting && setDeleteModal({ isOpen: false, post: null })}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <div className="pm-modal-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </div>
              <div>
                <h3>{t('adminDashboard.postModeration.confirmDeleteTitle', 'Delete Post')}</h3>
                <p>{t('adminDashboard.postModeration.confirmDeleteMessage', 'This action is permanent and cannot be undone. The author will be notified.')}</p>
              </div>
            </div>

            <div className="pm-modal-body">
              <div className="pm-reason-picker">
                <label>{t('adminDashboard.postModeration.deleteReasonLabel', 'Select reason(s) for deletion')}</label>
                <div className="pm-chips">
                  {REPORT_REASONS.map(({ value, label }) => {
                    const selected = deleteReasons.includes(value);
                    return (
                      <button
                        key={value}
                        type="button"
                        className={`pm-chip ${selected ? 'selected' : ''}`}
                        onClick={() => toggleReason(value)}
                        disabled={isDeleting}
                      >
                        {selected && (
                          <svg className="pm-chip-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                        <span>{label}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="pm-other-wrap open">
                  <textarea
                    rows={2}
                    value={otherReasonText}
                    onChange={(e) => setOtherReasonText(e.target.value)}
                    placeholder={t('adminDashboard.postModeration.otherReasonPlaceholder', 'Please specify the reason...')}
                    disabled={isDeleting}
                  />
                </div>

                {/* Image Upload */}
                <div className="pm-evidence">
                  <label className="pm-evidence-label">
                    {t('moderation.evidenceImages', 'Evidence Images')}
                    <span className="pm-evidence-optional">{t('common.optional', 'Optional')} • {deleteImages.length}/5</span>
                  </label>
                  <div className="pm-image-grid">
                    {deleteImagePreviews.map((preview, index) => (
                      <div key={index} className="pm-image-thumb">
                        <img src={preview} alt="" />
                        <button
                          type="button"
                          className="pm-image-remove"
                          onClick={() => removeDeleteImage(index)}
                          disabled={isDeleting}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {deleteImages.length < 5 && (
                      <button
                        type="button"
                        className="pm-image-add"
                        onClick={() => deleteFileInputRef.current?.click()}
                        disabled={isDeleting}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={deleteFileInputRef}
                    onChange={handleDeleteImageSelect}
                    accept="image/*"
                    multiple
                    className="pm-file-input"
                  />
                </div>

                {deleteReasons.length > 0 && (
                  <div className="pm-selection-count">
                    {deleteReasons.length} {t('adminDashboard.postModeration.reasonsSelected', 'reason(s) selected')}
                  </div>
                )}
              </div>

              {deleteError && (
                <div className="pm-modal-error">{deleteError}</div>
              )}

              <div className="pm-modal-actions">
                <button
                  className="pm-btn"
                  onClick={() => setDeleteModal({ isOpen: false, post: null })}
                  disabled={isDeleting}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  className="pm-btn pm-btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting || deleteReasons.length === 0}
                >
                  {isDeleting ? t('common.deleting', 'Deleting...') : t('adminDashboard.postModeration.deleteConfirm', 'Delete Post')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ignore Reports Confirmation Modal */}
      {ignoreConfirmOpen && (
        <div className="pm-modal-overlay" onClick={() => !isActionProcessing && setIgnoreConfirmOpen(false)}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <div className="pm-modal-icon is-muted">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div>
                <h3>{t('moderation.actions.ignoreConfirmTitle', 'Ignore Reports')}</h3>
                <p>{t('moderation.actions.ignorePostConfirmMessage', 'All reports on this post will be removed and it will drop out of the reported queue.')}</p>
              </div>
            </div>
            <div className="pm-modal-actions">
              <button
                className="pm-btn"
                onClick={() => setIgnoreConfirmOpen(false)}
                disabled={isActionProcessing}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="pm-btn"
                onClick={async () => {
                  setIgnoreConfirmOpen(false);
                  await handleAction('ignore');
                }}
                disabled={isActionProcessing}
              >
                {isActionProcessing
                  ? t('common.saving', 'Saving...')
                  : t('moderation.actions.ignore', 'Ignore Reports')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Error Toast */}
      {deleteError && (
        <div className="pm-toast">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default PostModerationManager;
